import React, { FC } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import Select, { Props as SelectProps } from 'react-select';

interface Option {
  label: string;
  value: any;
}

interface CustomSelectProps extends SelectProps {
  label?: string;
  error?: string;
  touched?: boolean;
  name: string;
  options: Option[];
  value?: any;
  onChange?: (value: any) => void;
  setFieldValue?: (field: string, value: any) => void;
  className?: string;
}

const SelectInput: FC<CustomSelectProps> = ({
  label,
  error,
  touched,
  name,
  options,
  value,
  onChange,
  setFieldValue,
  className,
  placeholder = 'Select...',
  ...props
}) => {
  // Handle change for Formik or standalone
  const handleChange = (option: any) => {
    const val = Array.isArray(option) ? option.map((o) => o.value) : option?.value;
    
    if (setFieldValue) {
      setFieldValue(name, val);
    }
    
    if (onChange) {
      onChange(val);
    }
  };

  // Determine current value object(s) for react-select
  const getValue = () => {
    if (props.isMulti && Array.isArray(value)) {
       return options.filter(o => value.includes(o.value));
    }
    return options.find(o => o.value === value) || null;
  };

  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: state.isDisabled ? 'var(--color-bg-tertiary)' : 'var(--color-bg-secondary)',
      borderColor: (() => {
        if (error && touched) return 'var(--color-error)';
        if (state.isFocused) return 'var(--color-primary)';
        return 'var(--color-border)';
      })(),
      borderRadius: '0.75rem',
      padding: '2px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(16, 185, 129, 0.2)' : 'none',
      '&:hover': {
        borderColor: error && touched ? 'var(--color-error)' : 'var(--color-border-dark)', 
      },
      minHeight: '48px',
      color: 'var(--color-text-primary)',
    }),
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: 'var(--color-surface-elevated)',
      borderRadius: '0.75rem',
      overflow: 'hidden',
      zIndex: 9999,
      marginTop: '4px',
      boxShadow: 'var(--shadow-lg)',
      border: '1px solid var(--color-border-light)',
    }),
    menuPortal: (provided: any) => ({
      ...provided,
      zIndex: 9999,
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      backgroundColor: (() => {
        if (state.isSelected) return 'var(--color-primary)';
        if (state.isFocused) return 'var(--color-bg-tertiary)'; 
        return 'transparent';
      })(),
      color: state.isSelected ? 'var(--color-white)' : 'var(--color-text-primary)',
      cursor: 'pointer',
      '&:active': {
        backgroundColor: 'var(--color-primary)',
      },
    }),
    input: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
    }),
    singleValue: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-primary)',
    }),
    placeholder: (provided: any) => ({
      ...provided,
      color: 'var(--color-text-muted)',
    }),
  };

  return (
    <div className={`w-full ${className}`}>
      {label && (
        <label htmlFor={name} className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1.5'>
          {label}
        </label>
      )}
      <Select
        id={name}
        name={name}
        options={options}
        value={getValue()}
        onChange={handleChange}
        styles={customStyles}
        classNamePrefix='react-select'
        placeholder={placeholder}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition='fixed'
        {...props}
      />
      {touched && error && (
        <div className='text-red-500 text-sm mt-1 animate-in slide-in-from-top-1'>{error}</div>
      )}
    </div>
  );
};

export default SelectInput;
